import { UIEvent } from 'react';
import * as React from 'react';
import ParagraphSuggestion from './ParagraphSuggestion/ParagraphSuggestion';
import {
    IParagraphSuggestionsProps,
    IParagraphSuggestionsState,
    LOAD_MORE_SUGGESTIONS_DISTANCE,
    PAGE_SIZE,
} from './ParagraphSuggestions-types';
import './ParagraphSuggestions.scss';
import { motion } from 'framer-motion';
import IconButton from '@mui/material/IconButton';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { IPropertySuggestion } from './ParagraphSuggestion/ParagraphSuggestion-types';
import { mapSuggestion } from '../../../../App/App-mappers';
import SuggestionResource from '../../../../../resources/SuggestionResource';
import { HighlightIndexType } from '../../../../Contract/Contract-types';
import { Alert } from '@thought-river/ui-components';
import { debounce } from 'lodash';
import { animationProps } from '../../IssuePaneTabs-helpers';
import {
    BannerClauseType,
    BannerType,
} from '../../../../HeatmapPane/HeatmapPane-types';

export default class ParagraphSuggestions extends React.Component<
    IParagraphSuggestionsProps,
    IParagraphSuggestionsState
> {
    private readonly listRef: React.RefObject<HTMLDivElement>;

    constructor(props: IParagraphSuggestionsProps) {
        super(props);

        this.state = {
            editedSuggestionId: null!, // Fixme: null checks
        };

        this.listRef = React.createRef();
    }

    componentDidMount() {
        this.props.resetSuggestions();
        this.loadFirstPageOfSuggestions();
    }

    componentDidUpdate = (prevProps: IParagraphSuggestionsProps) => {
        const {
            clauseNumbers,
            count,
            highlightedParagraphs,
            issue,
            resetSuggestions,
            setHighlightIndex,
            suggestions,
        } = this.props;

        if (prevProps?.issue?.id !== issue?.id) {
            resetSuggestions();
            this.loadFirstPageOfSuggestions();
        }

        const { scrollHeight, clientHeight } = this.listRef.current ?? {};
        if (
            scrollHeight !== undefined &&
            clientHeight !== undefined &&
            scrollHeight <= clientHeight &&
            suggestions.length < count
        ) {
            this.loadNextPage();
        }

        if (
            clauseNumbers.toString() !== prevProps.clauseNumbers.toString() &&
            !clauseNumbers.find(
                (clauseNumber) =>
                    clauseNumber.originUuid ===
                    highlightedParagraphs[0]?.originUuid
            )
        ) {
            // The currently selected clause number has been removed from the issue, so we need to unselect that paragraph
            setHighlightIndex(-1, HighlightIndexType.START);
            setHighlightIndex(-1, HighlightIndexType.END);
        }
    };

    componentWillUnmount() {
        this.props.resetSuggestions();
    }

    private async loadFirstPageOfSuggestions() {
        const {
            issue,
            nextPageNumber,
            setSuggestionsLoading,
            setSuggestions,
            setSuggestionsInitialLoadCompleted,
            streamCode,
        } = this.props;

        if (issue?.nonAssessedPropertyIds[0]) {
            setSuggestionsInitialLoadCompleted(false);
            setSuggestionsLoading(true);

            const suggestionData =
                await SuggestionResource.getSuggestionsAndTotalCountForProperty(
                    streamCode,
                    issue.nonAssessedPropertyIds[0],
                    1,
                    PAGE_SIZE
                );

            setSuggestions(
                suggestionData.suggestions.data.map(mapSuggestion),
                nextPageNumber + 1,
                suggestionData.count
            );
        }

        setSuggestionsLoading(false);
        setSuggestionsInitialLoadCompleted(true);
    }

    private onScroll = (e: UIEvent<HTMLDivElement>) => {
        const { count, suggestions, loading } = this.props;
        const isBottom =
            // @ts-ignore
            e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight <
            LOAD_MORE_SUGGESTIONS_DISTANCE;
        if (isBottom && count > suggestions.length && !loading) {
            this.loadNextPage();
        }
    };

    private loadNextPage = debounce(async () => {
        const {
            issue,
            nextPageNumber,
            suggestions,
            setSuggestions,
            setSuggestionsLoading,
            loading,
            streamCode,
        } = this.props;

        if (issue?.nonAssessedPropertyIds[0] && !loading) {
            setSuggestionsLoading(true);

            const suggestionData =
                await SuggestionResource.getSuggestionsForProperty(
                    streamCode,
                    issue.nonAssessedPropertyIds[0],
                    nextPageNumber,
                    PAGE_SIZE
                );

            setSuggestions(
                [...suggestions, ...suggestionData.data.map(mapSuggestion)],
                nextPageNumber + 1
            );
        }
    }, 500);

    private navigateToLocation(delta: 1 | -1) {
        const {
            highlightedParagraphs,
            clauseNumbers,
            setBannerSettings,
            issue,
        } = this.props;

        const currentSelection = highlightedParagraphs[0];
        let targetIndex = currentSelection
            ? clauseNumbers.findIndex(
                  (cn) => cn.index === currentSelection.index
              ) + delta
            : 0;
        targetIndex = targetIndex >= clauseNumbers.length ? 0 : targetIndex;
        targetIndex = targetIndex < 0 ? clauseNumbers.length - 1 : targetIndex;

        if (clauseNumbers[targetIndex]) {
            this.scrollToParagraph(clauseNumbers[targetIndex].id);
        }

        setBannerSettings({
            type: BannerType.INFO,
            clauseType: issue.paragraphIds.length
                ? BannerClauseType.PROPERTY_LINKED
                : BannerClauseType.PROPERTY_RELATED,
            currentItem: clauseNumbers[targetIndex].id,
            items: clauseNumbers.map((clause) => clause.id),
        });
    }

    private scrollToParagraph(paragraphId: string) {
        const { paragraphs, setSelectedParagraph, setHighlightIndex } =
            this.props;

        const paragraph = paragraphs.find((p) => p.id === paragraphId);

        if (paragraph) {
            setHighlightIndex(paragraph.index, HighlightIndexType.START);
            setHighlightIndex(paragraph.index, HighlightIndexType.END);
            setSelectedParagraph(paragraph);
        }
    }

    private renderHeader() {
        const { clauseNumbers, count, highlightedParagraphs, paragraphs } =
            this.props;

        const currentLocation = clauseNumbers.findIndex(
            (cn) => cn.originUuid === highlightedParagraphs[0]?.originUuid
        );

        return (
            <>
                <div>
                    {count} Drafting option{count > 1 ? 's' : ''}
                </div>
                <div className="suggestions-locations">
                    {currentLocation !== -1 ? currentLocation + 1 : 0} of{' '}
                    {clauseNumbers.length} suggested locations
                    <IconButton
                        className="location-button"
                        disabled={!paragraphs.length}
                        onClick={() => this.navigateToLocation(-1)}
                    >
                        <KeyboardArrowUp />
                    </IconButton>
                    <IconButton
                        className="location-button"
                        disabled={!paragraphs.length}
                        onClick={() => this.navigateToLocation(1)}
                    >
                        <KeyboardArrowDown />
                    </IconButton>
                </div>
            </>
        );
    }

    private suggestionSelected(suggestion: IPropertySuggestion) {
        const { issue, setSelectedIssue, updateIssue } = this.props;

        const updatedIssue = { ...issue, selectedSuggestion: suggestion };
        updateIssue(updatedIssue);
        setSelectedIssue(updatedIssue);
    }

    render() {
        const { initialLoadCompleted, issue, loading, suggestions } =
            this.props;

        const { editedSuggestionId } = this.state;

        return (
            <div className="paragraph-suggestions">
                {initialLoadCompleted ? (
                    suggestions.length ? (
                        <>
                            <motion.div
                                className="paragraph-suggestions-header"
                                {...animationProps(0.1)}
                            >
                                {this.renderHeader()}
                            </motion.div>
                            <div
                                className="paragraph-suggestions-list"
                                ref={this.listRef}
                                onScroll={this.onScroll}
                            >
                                {suggestions.map((suggestion, i) => (
                                    <motion.div
                                        key={`suggestion-${i}`}
                                        {...animationProps(0.2 + i / 10)}
                                    >
                                        <ParagraphSuggestion
                                            editing={
                                                editedSuggestionId ===
                                                suggestion.id
                                            }
                                            isSelected={
                                                issue?.selectedSuggestion
                                                    ? issue.selectedSuggestion
                                                          ?.id === suggestion.id
                                                    : i === 0
                                            }
                                            onClickCallback={(
                                                suggestionId: string
                                            ) => {
                                                this.setState({
                                                    editedSuggestionId:
                                                        suggestionId,
                                                });
                                            }}
                                            onSuggestionSelectedCallback={() =>
                                                this.suggestionSelected(
                                                    suggestion
                                                )
                                            }
                                            suggestion={suggestion}
                                            suggestionNumber={i + 1}
                                        />
                                    </motion.div>
                                ))}
                                {loading && (
                                    <div className="loading-more-wrapper">
                                        <span className="loader" />
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <motion.div
                            className="missing-suggestions-alert"
                            {...animationProps(0.1)}
                        >
                            <Alert severity="info">
                                This property has not been configured with
                                drafting options. If you think that it should
                                be, please contact your administrator.
                            </Alert>
                        </motion.div>
                    )
                ) : (
                    <div className="loading-wrapper">
                        <span className="loader" />
                    </div>
                )}
            </div>
        );
    }
}
